import { Camera, Upload } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  type Identifier,
} from "ra-core";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";

type Attachment = { src: string; title?: string; type?: string };
type NoteWithAttachments = {
  id: Identifier;
  date: string;
  attachments?: Attachment[] | null;
};

const isImage = (attachment: Attachment) =>
  attachment.type?.startsWith("image/") ||
  /\.(jpe?g|png|webp|gif|heic)$/i.test(attachment.title ?? attachment.src);

/**
 * Photos the customer sent (usually by WhatsApp) to check the installation is
 * possible. They are stored as attachments of a contact note, so they also
 * appear in the contact timeline.
 */
export const ClientPhotos = ({
  contactId,
  onChange,
}: {
  contactId: Identifier;
  onChange?: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: notes = [], refetch } = useGetList<NoteWithAttachments>(
    "contact_notes",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "date", order: "DESC" },
    },
  );
  const attachments = notes.flatMap((note) => note.attachments ?? []);
  const photos = attachments.filter(isImage);
  const otherFiles = attachments.filter((attachment) => !isImage(attachment));

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      await dataProvider.create("contact_notes", {
        data: {
          contact_id: contactId,
          text: "Fotos para validar la instalación",
          date: new Date().toISOString(),
          sales_id: identity?.id,
          attachments: Array.from(files).map((file) => ({
            src: URL.createObjectURL(file),
            title: file.name,
            rawFile: file,
          })),
        },
      });
      notify("Fotos subidas", { type: "success" });
      await refetch();
      onChange?.();
    } catch (error) {
      console.error(error);
      notify("No se han podido subir las fotos", { type: "error" });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <Camera className="h-4 w-4" />
          Fotos de la instalación
        </h4>
        <Button
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="h-4 w-4" />
          {uploading ? "Subiendo…" : "Subir fotos"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(event) => handleFiles(event.target.files)}
        />
      </div>
      {photos.length ? (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <a
              key={photo.src}
              href={photo.src}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={photo.src}
                alt={photo.title ?? "Foto"}
                className="aspect-square w-full rounded-md object-cover"
              />
            </a>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Todavía no hay fotos. Sube las que el cliente mande por WhatsApp.
        </p>
      )}
      {otherFiles.map((file) => (
        <a
          key={file.src}
          href={file.src}
          target="_blank"
          rel="noreferrer"
          className="text-sm underline"
        >
          {file.title ?? "Archivo"}
        </a>
      ))}
    </section>
  );
};
